import {effect, inject, Injectable, signal} from "@angular/core";
import {Subject} from "rxjs";
import {Location} from "@angular/common";
import {filter} from "rxjs/operators";
import {NavigationStart, Router} from "@angular/router";
import {Preference, PreferencesService} from "src/app/core/services/services/preferences.service";
import {isUndefined} from "lodash";
import {AppearanceHelperZoom} from "src/app/core/services/services/appearance-service.d/appearance-helper-zoom";

@Injectable({
	providedIn: "root",
})
export class SidenavControllerService {
	private readonly location = inject(Location);
	private readonly router = inject(Router);
	private readonly preferencesService = inject(PreferencesService);
	private readonly appearanceHelperZoom = inject(AppearanceHelperZoom);

	public sidenavStatusChange = new Subject<boolean>();

	public sidenavStateExpanded = signal<boolean>(false);
	public sidenavStateHidden = signal<boolean>(false);

	private readonly isAutomaticModeSwitchingDisabled = signal(false);

	constructor() {
		this.publishStateChanges();
		this.registerSidenavHiding();
	}

	private publishStateChanges() {
		effect(() => {
			//      publishes change to the sidenav component
			this.sidenavStatusChange.next(this.sidenavStateExpanded());
		});
	}

	public toggleSidebar() {
		this.sidenavStateExpanded.update((value) => {
			value = !value;
			return value;
		});
		if (!this.isAutomaticModeSwitchingDisabled()) {
			this.isAutomaticModeSwitchingDisabled.set(true);
		}
		this.preferencesService.put(Preference.SIDEBAR_EXPANDED, String(this.sidenavStateExpanded())).then();
	}

	public async setInitialState() {
		this.sidenavStateExpanded.update((value) => {
			//note that router doesn't have the correct URL yet here, so we need Location instead
			value = ["", "/homescreen"].includes(this.location.path());
			return value;
		});

		let sidebarStateUser = await this.preferencesService.get(Preference.SIDEBAR_EXPANDED);

		if (!isUndefined(sidebarStateUser)) {
			this.isAutomaticModeSwitchingDisabled.set(true);
			this.sidenavStateExpanded.update((value) => {
				value = sidebarStateUser === "true";
				return value;
			});
			return;
		}

		this.registerOnPageChange();
	}

	private registerOnPageChange() {
		this.router.events
			.pipe(filter((event) => event instanceof NavigationStart))
			.subscribe((navigationStart: NavigationStart) => {
				if (!this.isAutomaticModeSwitchingDisabled()) {
					this.sidenavStateExpanded.update((value) => {
						value = navigationStart.url === "/homescreen";
						return value;
					});
				}
			});
	}

	private registerSidenavHiding() {
		effect(() => {
			if (this.sidenavStateHidden()) {
				document.body.classList.add("sidenav-hidden");
			} else {
				document.body.classList.remove("sidenav-hidden");
			}
		});
		this.appearanceHelperZoom.onRealWindowSizeChange.subscribe(() => {
			this.changeSidenavHiddenState();
		});
	}

	private changeSidenavHiddenState() {
		if (
			this.appearanceHelperZoom.windowWidth() / this.appearanceHelperZoom.zoom <= 1100 &&
			!this.sidenavStateHidden()
		) {
			this.sidenavStateHidden.set(true);
		} else if (
			this.appearanceHelperZoom.windowWidth() / this.appearanceHelperZoom.zoom > 1100 &&
			this.sidenavStateHidden()
		) {
			this.sidenavStateHidden.set(false);
		}
	}
}
